import { map, merge, pick, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import { Element, type SnippetElement } from '@@editor/helpers/Element';
import { isUnitySnippet, type UnitySnippet } from '@@editor/typings/UnityElements';
import addDefaultElementIfEmptyState from '@@editor/utils/addDefaultElementIfEmptyState';

import { CURRENT_VERSION } from '../constants';
import { type ElementRule, type Next } from '../types';

const deserializeContent =
    (next: Next<SnippetElement['data']['content'][number]>) => (content: UnitySnippet['content']) =>
        pipe(content, addDefaultElementIfEmptyState, map(next));

const serializeContent =
    (next: Next<UnitySnippet['content'][number]>) => (content: SnippetElement['data']['content']) =>
        pipe(content, map(next));

type Rule = ElementRule<SnippetElement, UnitySnippet>;

const snippetRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnitySnippet(element)) {
            invariant(next, 'Cannot deserialize Snippet without a `next` function');

            const { content, type } = element;

            return {
                type,
                data: merge(pick(element, ['content', 'templateElement']), {
                    ...(content && { content: deserializeContent(next)(content) }),
                }),
                children: [{ text: '' }],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isSnippetElement(node)) {
            invariant(next, 'Cannot serialize Snippet without a `next` function');

            const { data, type } = node;

            return {
                ...merge(pick(data, ['content', 'templateElement']), {
                    ...(data.content && { content: serializeContent(next)(data.content) }),
                }),
                version: CURRENT_VERSION,
                type,
            };
        }
    },
};

export default snippetRules;
